import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    Output,
    ViewChild
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
    AirspaceCheckerParametersWithStartEnd,
    MapAirspaceCheckerService
} from '@flyfreely-portal-ui/airspace';
import { CommonDialoguesService } from '@flyfreely-portal-ui/common-dialogues';
import {
    AirspaceJurisdictionDto,
    CreateLocationCommand,
    DO_NOTHING,
    DateFormatService,
    FEATURE_LOTAM,
    FlyFreelyError,
    FlyFreelyLoggingService,
    GeospatialService,
    LocationDetailsDto,
    LocationDto,
    LocationFeatureDto,
    LocationService,
    OperationsSummaryService,
    OrganisationService,
    PersonsOrganisationDto,
    PreferencesService,
    UpdateLocationCommand,
    WorkTracker,
    browserTimezone,
    hasFeatureFlag,
    toTimestamp
} from '@flyfreely-portal-ui/flyfreely';
import {
    FeatureGroup,
    MapDataInput,
    MapFeature,
    MapViewport
} from '@flyfreely-portal-ui/map';
import { ExternalMissionsDataService } from '@flyfreely-portal-ui/missions-shared';
import { MissionNoticesDataService } from '@flyfreely-portal-ui/notices-shared';
import { OperationsSearchDataService } from '@flyfreely-portal-ui/operations-shared';
import { SitesDataService } from '@flyfreely-portal-ui/sites-shared';
import { WorkspaceStateService } from '@flyfreely-portal-ui/workspace';
import center from '@turf/center';
import { Angulartics2 } from 'angulartics2';
import * as FileSaver from 'file-saver';
import { getOrElse } from 'fp-ts/es6/Option';
import { GeometryCollection } from 'geojson';
import { AirspaceCheckService } from 'libs/airspace/src/lib/airspace-check/airspace-check.service';
import { AIRSPACE_DISCLAIMER } from 'libs/locations/src/lib/interfaces';
import { mergeFeatureCollectionWithSprites } from 'libs/map/src/lib/flyfreely-map/helpers';
import { missionLocationsToFeatureCollection } from 'libs/missions/src/lib/helpers';
import moment from 'moment';
import { BsModalRef, BsModalService, ModalOptions } from 'ngx-bootstrap/modal';
import { BehaviorSubject, Subject, combineLatest } from 'rxjs';
import {
    debounceTime,
    distinctUntilChanged,
    filter,
    map as subjectMap,
    switchMap,
    take,
    takeUntil,
    tap
} from 'rxjs/operators';
import {
    GeometryFeatureGroups,
    GeometryUploadService,
    UploadedFeature,
    isSinglePolygon
} from '../geometry-upload.service';
import { showGeometryUploadDialogue } from '../geometry-upload/geometry-upload-dialogue.component';
import {
    GEOMETRY_AND_UGCS_UPLOAD_ACCEPT,
    getFeatureGroups,
    getFileName,
    toMapFeature
} from '../helpers';
import { LocationEditDrawingComponent } from '../location-edit-drawing/location-edit-drawing.component';
import { LocationEditService } from '../location-edit.service';
import { UgcsUploadService } from '../ugcs-upload.service';

// This mirrors the InitialCheckerParameters in the airspace library.
export interface MissionAirspaceParameters {
    id: number;
    date: Date;
    duration: number;
    rulesetIdentifier: string;
    height: number;
    timeZone?: string;
}

@Component({
    selector: 'location-edit-v2-dialogue',
    templateUrl: './location-edit-dialogue.component.html',
    styleUrls: ['./location-edit-dialogue.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        AirspaceCheckService,
        MapAirspaceCheckerService,
        LocationEditService,
        MissionNoticesDataService,
        UgcsUploadService,
        SitesDataService,
        ExternalMissionsDataService,
        OperationsSearchDataService
    ]
})
export class LocationEditV2Dialogue {
    @Input() locationId: number;
    @Input() organisationId: number;
    @Input() type: LocationDetailsDto.Type | LocationDto.Type =
        LocationDto.Type.TEMPLATE;
    @Input() missionValues: MissionAirspaceParameters;
    /**
     * These are the default values used to pre-populate a new location.
     * These are only used for new locations.
     */
    @Input() defaultValues: Partial<LocationDetailsDto>;
    @Input() canCreateMission: boolean;

    @Output() change = new EventEmitter<LocationDetailsDto>();
    @Output() featuresChange = new EventEmitter<FeatureGroup[]>();
    @Output() updatedMissionValues =
        new EventEmitter<MissionAirspaceParameters>();

    @ViewChild('drawingComponent')
    drawingComponent: LocationEditDrawingComponent;

    organisation: PersonsOrganisationDto;
    location: LocationDetailsDto;

    featureGroups: FeatureGroup[];

    canUseLotams$ = new BehaviorSubject<boolean>(false);

    mapData$ = new BehaviorSubject<MapDataInput>({});

    mapCenter$ = new BehaviorSubject<MapViewport>(null);
    timeZoneSource:
        | 'ORGANISATION'
        | 'MAP_CENTER'
        | 'FLIGHT_AREA'
        | 'AIRSPACE_CHECK' = 'ORGANISATION';
    currentTimeZone: string;

    formGroup: FormGroup;
    private ngUnsubscribe$ = new Subject<void>();

    workTracker = new WorkTracker();

    readonly GEOMETRY_AND_UGCS_UPLOAD_ACCEPT = GEOMETRY_AND_UGCS_UPLOAD_ACCEPT;

    isAddingFeature = false;
    working = false;

    hasRequiredFeatures = false;
    hasAnyFeatures = false;

    isEditing = false;

    /**
     * The current values in the airspace checker. This is managed by the location-airspace-check component
     */
    airspaceValue$ = new BehaviorSubject<AirspaceCheckerParametersWithStartEnd>(
        {
            airspaceDate: null,
            airspaceDuration: 0,
            airspaceHeight: 0,
            airspaceRuleset: null,
            startTime: null,
            endTime: null,
            airspaceTimeZone: null
        }
    );

    private hasChanged = false;
    private wasNameSetByUser = false;

    private nextId = 1;

    // Airspace Disclaimer
    showAirspaceDisclaimer = false;
    shouldShowAirspaceDisclaimer$ = new Subject<void>();
    airspaceDisclaimerDates: { [key: string]: Date } = {};
    mapJurisdiction: AirspaceJurisdictionDto;

    flightArea: GeoJSON.Polygon;

    constructor(
        private modal: BsModalRef<LocationEditV2Dialogue>,
        modalOptions: ModalOptions,
        private modalService: BsModalService,
        private locationService: LocationService,
        private geometryUploadService: GeometryUploadService,
        private workspaceStateService: WorkspaceStateService,
        private organisationService: OrganisationService,
        private locationEditService: LocationEditService,
        private preferencesService: PreferencesService,
        private geospatialService: GeospatialService,
        private airspaceCheckService: AirspaceCheckService,
        private missionNoticesDataService: MissionNoticesDataService,
        private commonDialoguesService: CommonDialoguesService,
        private operationsSummaryService: OperationsSummaryService,
        private logging: FlyFreelyLoggingService,
        private changeDetector: ChangeDetectorRef,
        private angulartics2: Angulartics2,
        private ugcsUploadService: UgcsUploadService,
        private dateFormatService: DateFormatService,
        private mapAirspaceCheckerService: MapAirspaceCheckerService,
        private operationsSearchDataService: OperationsSearchDataService
    ) {
        modalOptions.closeInterceptor = () => {
            this.drawingComponent?.handleEditOnClose();

            if (this.hasChanged || this.formGroup.dirty) {
                return this.commonDialoguesService.showConfirmationDialogue(
                    'Confirm Cancel',
                    `You have unsaved changes, are you sure you want to cancel?`,
                    'Yes',
                    () => Promise.resolve()
                );
            }
            return Promise.resolve();
        };

        this.formGroup = new FormGroup({
            name: new FormControl(undefined, [Validators.required])
        });

        combineLatest([
            this.workTracker.observable,
            this.locationEditService.working$
        ])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(([working, serviceWorking]) => {
                this.working = working || serviceWorking;
                this.changeDetector.markForCheck();
            });

        this.shouldShowAirspaceDisclaimer$
            .pipe(debounceTime(500), takeUntil(this.ngUnsubscribe$))
            .subscribe(() => this.showAirspaceDisclaimerDialogue());

        // Start the time zone used with the organisation default, falling back on the browser time zone.
        this.workspaceStateService.currentLoadedOrganisation$
            .pipe(
                filter(org => org != null),
                take(1)
            )
            .subscribe(
                organisation =>
                    (this.currentTimeZone =
                        organisation.organisation.timeZone ?? browserTimezone)
            );
    }

    ngOnInit() {
        combineLatest([
            this.workTracker.observable,
            this.locationEditService.working$
        ])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(([working, serviceWorking]) => {
                this.working = working || serviceWorking;
                this.changeDetector.markForCheck();
            });

        this.formGroup.controls.name.valueChanges
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(newValue => this.onFormChanges(newValue));
        this.refreshLocation();

        this.locationEditService.missionCreated$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => {
                this.hasChanged = false;
                this.formGroup.markAsPristine();
                this.modal.hide();
            });

        this.refreshPermissions();

        // Setup to keep LOTAM up to date - applies to all location types
        combineLatest([
            this.canUseLotams$,
            this.mapAirspaceCheckerService.currentParameters$
        ])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(([canUseLotams, parameters]) => {
                if (canUseLotams) {
                    const effectiveDate = parameters.airspaceDate ?? new Date();
                    const tz = parameters.airspaceTimeZone ?? null;
                    const start = tz
                        ? moment
                              .tz(effectiveDate, tz)
                              .startOf('day')
                              .toISOString()
                        : moment(effectiveDate).startOf('day').toISOString();
                    const end = tz
                        ? moment
                              .tz(effectiveDate, tz)
                              .endOf('day')
                              .toISOString()
                        : moment(effectiveDate).endOf('day').toISOString();
                    this.operationsSearchDataService.find(
                        { start, end },
                        this.organisationId
                    );
                } else {
                    this.missionNoticesDataService.resetNotices();
                }
            });

        if (this.type === LocationDto.Type.MISSION) {
            this.mapAirspaceCheckerService.timingParameters$
                .pipe(
                    debounceTime(500),
                    // Neighbouring missions only depend on the day window
                    // (date + timezone) and duration - not ruleset/height - so
                    // only re-query when the notices or those timing fields change.
                    distinctUntilChanged(
                        (prevValues, values) =>
                            (prevValues?.airspaceDate?.getTime() ?? null) ===
                                (values?.airspaceDate?.getTime() ?? null) &&
                            prevValues?.airspaceDuration ===
                                values?.airspaceDuration &&
                            prevValues?.airspaceTimeZone ===
                                values?.airspaceTimeZone
                    ),
                    switchMap(airspaceValues => {
                        const effectiveTimeZone =
                            airspaceValues.airspaceTimeZone ??
                            this.currentTimeZone ??
                            browserTimezone;
                        const now = moment(this.missionValues.date).tz(
                            effectiveTimeZone
                        );
                        const nowEnd = moment(now).endOf('day').toISOString();
                        const startTime =
                            airspaceValues?.airspaceDate != null
                                ? toTimestamp(
                                      moment(airspaceValues.airspaceDate)
                                          .tz(effectiveTimeZone)
                                          .startOf('day')
                                          .toDate(),
                                      airspaceValues.airspaceTimeZone
                                  )
                                : now.toISOString();
                        const endTime =
                            airspaceValues?.airspaceDate != null
                                ? toTimestamp(
                                      moment
                                          .tz(
                                              airspaceValues.airspaceDate,
                                              airspaceValues.airspaceTimeZone
                                          )
                                          .endOf('day')
                                          .toDate(),
                                      airspaceValues.airspaceTimeZone
                                  )
                                : nowEnd;
                        return this.operationsSummaryService.findMissionLocations(
                            {
                                organisationId: this.organisationId,
                                startTime,
                                endTime
                            }
                        );
                    })
                )
                .pipe(takeUntil(this.ngUnsubscribe$))
                .subscribe(missionLocationList => {
                    const [pointLocations, polygonLocations] =
                        missionLocationsToFeatureCollection(
                            missionLocationList.map(missionLocation => ({
                                ...missionLocation,
                                missionList: missionLocation.missionList.filter(
                                    mission =>
                                        this.missionValues == null ||
                                        mission.id !== this.missionValues.id
                                )
                            })),
                            this.dateFormatService
                        );
                    this.mapData$.next({
                        MISSIONS: polygonLocations,
                        MISSION_MARKERS: pointLocations
                    });
                });
        } else {
            this.missionNoticesDataService.lotams$
                .pipe(takeUntil(this.ngUnsubscribe$))
                .subscribe(lotams => {
                    this.mapData$.next({
                        ...this.mapData$.getValue(),
                        LOTAM: mergeFeatureCollectionWithSprites(
                            lotams.map(n => n.displayLocation)
                        )
                    });
                });
        }

        this.setupTimeZoneHandlers();

        this.refreshPermissions();
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
        this.mapData$.complete();
        this.shouldShowAirspaceDisclaimer$.complete();
        this.mapCenter$.complete();
        this.canUseLotams$.complete();
        this.airspaceValue$.complete();
    }

    // ---- Handlers for outputs from LocationEditDrawingComponent ----

    onFeatureGroupsChange(featureGroups: FeatureGroup[]) {
        this.featureGroups = featureGroups;
        this.hasChanged = true;
    }

    onHasRequiredFeaturesChange(value: boolean) {
        this.hasRequiredFeatures = value;
        this.changeDetector.markForCheck();
    }

    onHasAnyFeaturesChange(value: boolean) {
        this.hasAnyFeatures = value;
        this.changeDetector.markForCheck();
    }

    onFlightAreaChange(flightArea: GeoJSON.Polygon | null) {
        this.flightArea = flightArea;
        this.shouldShowAirspaceDisclaimer$.next();

        // Handle time zone source
        if (
            this.hasRequiredFeatures &&
            this.flightArea != null &&
            (this.timeZoneSource === 'ORGANISATION' ||
                this.timeZoneSource === 'MAP_CENTER')
        ) {
            this.timeZoneSource = 'FLIGHT_AREA';
            const flightAreaCenter = center(this.flightArea);
            this.mapCenter$.next({
                latitude: flightAreaCenter.geometry.coordinates[1],
                longitude: flightAreaCenter.geometry.coordinates[0],
                zoom: null,
                bounds: null
            });
        } else {
            this.timeZoneSource =
                this.timeZoneSource === 'ORGANISATION' ||
                this.timeZoneSource === 'MAP_CENTER'
                    ? this.timeZoneSource
                    : (this.timeZoneSource = 'MAP_CENTER');
        }
    }

    onGeocodeResultSelected(feature: MapFeature) {
        if (!this.wasNameSetByUser) {
            this.formGroup.patchValue(
                { name: feature.name },
                { emitEvent: false }
            );
        }
    }

    onViewportChange(viewport: MapViewport) {
        if (this.timeZoneSource === 'MAP_CENTER') {
            this.mapCenter$.next(viewport);
        }
        if (
            this.timeZoneSource === 'ORGANISATION' &&
            this.drawingComponent?.map?.areMapFeaturesReady
        ) {
            this.timeZoneSource = 'MAP_CENTER';
        }
    }

    onMapJurisdictionChanged(jurisdiction: AirspaceJurisdictionDto) {
        this.mapJurisdiction = jurisdiction;
        this.showAirspaceDisclaimer = false;
        this.shouldShowAirspaceDisclaimer$.next();
    }

    onAirspaceValuesUpdated(values: AirspaceCheckerParametersWithStartEnd) {
        this.airspaceValue$.next(values);
    }

    onAirspaceDisclaimerAcknowledged() {
        this.showAirspaceDisclaimer = false;
        const airspaceJurisdictionId =
            this.location?.airspaceJurisdiction?.id ??
            this.mapJurisdiction?.id ??
            this.drawingComponent?.map?.currentlySelectedJurisdictionId;
        this.preferencesService
            .updatePreferences(AIRSPACE_DISCLAIMER, null, {
                ...this.airspaceDisclaimerDates,
                [airspaceJurisdictionId]: new Date()
            })
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => DO_NOTHING)
            .add(this.workTracker.createTracker());
    }

    onEditModeChanged(isEditingOrEvent: boolean | string) {
        this.hasChanged = true;
        // If we were editing and the mode ends, trigger save retry when appropriate
        if (isEditingOrEvent === false && this.isEditing) {
            // The drawing component already updated isEditing; check if we can proceed with save
        }
    }

    onIsAddingFeatureChange(value: boolean) {
        this.isAddingFeature = value;
        this.changeDetector.markForCheck();
    }

    onIsEditingChange(value: boolean) {
        this.isEditing = value;
        this.changeDetector.markForCheck();
    }

    // ---- Location data ----

    private refreshLocation() {
        if (this.locationId != null) {
            this.locationService
                .findLocation(this.locationId, this.organisationId)
                .pipe(takeUntil(this.ngUnsubscribe$))
                .subscribe(location => this.setLocation(location))
                .add(this.workTracker.createTracker());
        } else {
            if (this.defaultValues != null) {
                this.formGroup.patchValue({
                    name: this.defaultValues.name
                });

                this.setFeatures(this.defaultValues.features ?? []);
            } else {
                this.setFeatures([]);
            }
        }
    }

    refreshPermissions() {
        this.organisationService
            .findByIdForUser(this.organisationId, this.organisationId)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(organisation => {
                this.organisation = organisation;
                this.canUseLotams$.next(
                    hasFeatureFlag(organisation, FEATURE_LOTAM)
                );
            });
    }

    /**
     * To ensure the map has the most accurate time zone as the user navigates and plans
     * these handlers update the time zone used based on the most authoritative source.
     */
    private setupTimeZoneHandlers() {
        this.mapCenter$
            .pipe(
                filter(viewport => viewport != null),
                debounceTime(1000),
                switchMap(viewport =>
                    this.geospatialService.findTimeZone(
                        viewport.latitude,
                        viewport.longitude
                    )
                ),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe({
                next: timezone => {
                    this.currentTimeZone =
                        timezone?.timeZone != null
                            ? timezone.timeZone
                            : this.currentTimeZone;
                    this.changeDetector.markForCheck();
                },
                error: (error: FlyFreelyError) => DO_NOTHING
            });

        this.airspaceCheckService.result$
            .pipe(
                filter(
                    check =>
                        check != null &&
                        this.timeZoneSource !== 'AIRSPACE_CHECK'
                ),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe(result => {
                this.timeZoneSource = 'AIRSPACE_CHECK';
                this.currentTimeZone = result.timeZone;
                this.changeDetector.detectChanges();
            });
    }

    private setLocation(location: LocationDetailsDto) {
        this.formGroup.patchValue(
            { name: location.name },
            { emitEvent: false }
        );
        this.wasNameSetByUser = true;

        this.location = location;

        this.setFeatures(location.features);
    }

    private setFeatures(locationFeatures: LocationFeatureDto[]) {
        const { features, nextId } = getFeatureGroups(
            locationFeatures,
            ++this.nextId,
            0
        );
        this.featureGroups = features;
        this.nextId = nextId + 1;
    }

    private addFeature(locationFeature: LocationFeatureDto) {
        this.featureGroups = this.featureGroups.map(fg =>
            fg.categories.findIndex(c => c.id === locationFeature.type) !== -1
                ? {
                      ...fg,
                      existingFeatures: fg.existingFeatures.concat(
                          toMapFeature(locationFeature, this.nextId++)
                      )
                  }
                : fg
        );

        // Zoom to show all created features
        if (this.drawingComponent) {
            this.drawingComponent.zoomToAllFeatures(false);
        }
    }

    onFormChanges(newValue: { name: string }) {
        this.wasNameSetByUser = true;
    }

    // ---- KML / Upload ----

    uploadLocation(locationFiles: File) {
        const locationFile = locationFiles;

        if (
            locationFile.type === 'application/json' ||
            locationFile.name.toLowerCase().endsWith('.json')
        ) {
            this.uploadUgCSJSON(locationFile);
            return;
        }

        this.locationService
            .uploadLocation(locationFile)
            .pipe(
                takeUntil(this.ngUnsubscribe$),
                take(1),
                tap({
                    next: DO_NOTHING,
                    error: (error: FlyFreelyError) =>
                        this.logging.error(
                            error,
                            `Error uploading geometry file: ${error.message}`
                        )
                })
            )
            .subscribe(featureCollection => {
                const features =
                    this.geometryUploadService.parseGeometryGroupings(
                        featureCollection
                    );
                if (features.length === 0) {
                    this.logging.error(
                        null,
                        `The uploaded file does not contain any valid location geometry.`
                    );
                } else if (isSinglePolygon(features)) {
                    this.parseSingleGeometryFeature(
                        features[0].features[0],
                        locationFile.name
                    );
                } else {
                    showGeometryUploadDialogue(
                        this.modalService,
                        features,
                        locationFile.name
                    )
                        .content.selectedFeatures.pipe(
                            takeUntil(this.ngUnsubscribe$)
                        )
                        .subscribe(features =>
                            this.parseComplexGeometry(
                                features,
                                locationFile.name
                            )
                        );
                }
            })
            .add(this.workTracker.createTracker());
    }

    parseSingleGeometryFeature(feature: UploadedFeature, fileName: string) {
        this.addFeature({
            name: fileName,
            type: feature.featureType,
            geometry: <GeoJSON.Polygon>feature.geometry
        } as LocationFeatureDto);

        if (!this.wasNameSetByUser) {
            this.formGroup.patchValue(
                { name: feature.name },
                { emitEvent: false }
            );
        }

        this.angulartics2.eventTrack.next({
            action: 'kml-single-feature-import',
            properties: {
                category: `kml-upload`
            }
        });
    }

    parseComplexGeometry(features: GeometryFeatureGroups[], name: string) {
        features.forEach(group => {
            group.features.forEach(f => {
                this.addFeature({
                    name: f.name,
                    type: f.featureType,
                    geometry: f.geometry,
                    properties: f.properties
                } as LocationFeatureDto);
            });
        });

        if (!this.wasNameSetByUser) {
            this.formGroup.patchValue({ name: name }, { emitEvent: false });
        }
    }

    uploadUgCSJSON(locationFile: File) {
        const reader = new FileReader();

        reader.onloadend = e => {
            try {
                if (e.target?.readyState === FileReader.DONE) {
                    const jsonString = e.target?.result as string;

                    const validationResult =
                        this.ugcsUploadService.validateAndParseJSON(jsonString);

                    const locationFeatures =
                        this.ugcsUploadService.convertToLocationFeatures(
                            validationResult.data
                        );

                    const currentLocationName =
                        this.formGroup.get('name')?.value;
                    if (
                        currentLocationName == null ||
                        currentLocationName.trim() === ''
                    ) {
                        const missionName =
                            this.ugcsUploadService.getMissionName(
                                validationResult.data
                            );
                        if (missionName) {
                            this.formGroup.patchValue(
                                { name: missionName },
                                { emitEvent: false }
                            );
                        }
                    }

                    locationFeatures.forEach(feature => {
                        this.addFeature(feature);
                    });

                    this.changeDetector.markForCheck();

                    this.logging.success(
                        `Successfully imported ${locationFeatures.length} features from UgCS mission plan`
                    );
                }
            } catch (error) {
                this.logging.error(
                    error,
                    `Error processing uploaded file: ${error.message}`
                );
            }
        };

        reader.onerror = error => {
            this.logging.error(error, `Error reading uploaded location file`);
        };

        reader.readAsText(locationFile, 'UTF-8');
    }

    // ---- Airspace disclaimer ----

    showAirspaceDisclaimerDialogue() {
        const disclaimer =
            this.location?.airspaceJurisdiction?.airspaceDisclaimer ??
            this.mapJurisdiction?.airspaceDisclaimer ??
            this.drawingComponent?.map?.currentlySelectedJurisdiction
                ?.airspaceDisclaimer;
        const airspaceJurisdictionId =
            this.location?.airspaceJurisdiction?.id ??
            this.mapJurisdiction?.id ??
            this.drawingComponent?.map?.currentlySelectedJurisdictionId;
        if (
            airspaceJurisdictionId == null ||
            disclaimer == null ||
            disclaimer.length == 0
        ) {
            this.showAirspaceDisclaimer = false;
            this.changeDetector.markForCheck();
            return;
        }
        this.mapJurisdiction =
            this.mapJurisdiction ?? this.location?.airspaceJurisdiction;
        this.preferencesService
            .findPreferencesAsOption(AIRSPACE_DISCLAIMER, null)
            .pipe(
                subjectMap(p =>
                    getOrElse(() => ({
                        [airspaceJurisdictionId]: moment(new Date())
                            .subtract(1, 'day')
                            .toDate()
                    }))(p)
                ),
                takeUntil(this.ngUnsubscribe$),
                take(1)
            )
            .subscribe({
                next: preferences => {
                    this.airspaceDisclaimerDates = preferences;
                    if (
                        preferences == null ||
                        preferences[airspaceJurisdictionId] == null
                    ) {
                        this.showAirspaceDisclaimer = true;
                        this.changeDetector.markForCheck();
                        return;
                    }
                    const today = moment(new Date()).format('YYYY-MM-DD');
                    const lastViewed = moment(
                        preferences[airspaceJurisdictionId]
                    ).format('YYYY-MM-DD');
                    this.showAirspaceDisclaimer =
                        moment(lastViewed).isBefore(today);
                    this.changeDetector.markForCheck();
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(error);
                }
            });
    }

    // ---- Save / Create ----

    save() {
        if (this.drawingComponent?.hasInvalidFeatures()) {
            this.showInvalidFeaturesConfirmation(() => this.performSave());
            return;
        }
        this.performSave();
    }

    private showInvalidFeaturesConfirmation(onConfirm: () => void) {
        const invalidCount = this.drawingComponent?.invalidFeatures?.size ?? 0;
        const message = `This location contains ${invalidCount} invalid feature${
            invalidCount > 1 ? 's' : ''
        }. Invalid features may cause issues with mission planning and execution. Are you sure you want to save?`;
        this.commonDialoguesService
            .showConfirmationDialogue(
                'Invalid Features Detected',
                message,
                'Save Anyway',
                () => {
                    onConfirm();
                    return Promise.resolve();
                }
            )
            .catch(DO_NOTHING);
    }

    private performSave() {
        if (this.isAddingFeature) {
            this.logging.warn(
                `Please finish adding the current feature before saving.`
            );
            return;
        }
        if (this.isEditing) {
            this.drawingComponent.doneEditing();
            // onEditModeChanged will fire; wait for the next call to save() after editing finishes
            if (this.hasRequiredFeatures) {
                return this.save();
            } else {
                this.logging.error(
                    null,
                    `Location template does not meet all saving requirements. Please ensure there is a flight area and location name and try again.`
                );
                return;
            }
        }

        const requiredFeatureGroups = (
            this.drawingComponent?.getFeatureGroups() ?? this.featureGroups
        ).filter(g =>
            g.existingFeatures.reduce(
                (acc, f) =>
                    acc ||
                    ((f.categoryId === 'FLIGHT_AREA' ||
                        f.categoryId === 'OFFSET_FLIGHT_AREA' ||
                        f.categoryId === 'CIRCLE_FLIGHT_AREA') &&
                        f.geom != null),
                false
            )
        );
        if (requiredFeatureGroups.length == 0) {
            return;
        }

        let polygon;
        if (
            requiredFeatureGroups[0].existingFeatures[0].geom.type ===
            'GeometryCollection'
        ) {
            const collection = requiredFeatureGroups[0].existingFeatures[0]
                .geom as GeometryCollection;
            polygon = collection.geometries.find(g => g.type === 'Polygon');
        }
        const geom =
            polygon ?? requiredFeatureGroups[0].existingFeatures[0].geom;
        if (geom.type !== 'Polygon') {
            return;
        }

        const airspaceValue = this.airspaceValue$.getValue();
        const outputMissionValues: MissionAirspaceParameters = {
            id: this.missionValues?.id,
            date: airspaceValue?.airspaceDate,
            duration: airspaceValue?.airspaceDuration,
            rulesetIdentifier: airspaceValue?.airspaceRuleset?.identifier,
            height: airspaceValue?.airspaceHeight
        };

        const newCopy =
            this.location != null &&
            this.location.id != null &&
            !this.location.availableActions.canEdit;

        const name = this.formGroup.value.name;
        const features = this.getFeatures();

        if (this.locationId != null && !newCopy) {
            const updateCommand: UpdateLocationCommand = {
                name,
                features,
                derivedFromId: this.location?.derivedFromId
            };
            this.locationService
                .updateLocation(this.locationId, updateCommand)
                .pipe(takeUntil(this.ngUnsubscribe$))
                .subscribe({
                    next: result => {
                        this.hasChanged = false;
                        this.formGroup.markAsPristine();
                        this.logging.success(
                            `Location updated${
                                this.missionValues != null
                                    ? ' & mission details updated'
                                    : ''
                            }`
                        );
                        this.change.emit(result);
                        if (this.missionValues != null) {
                            this.updatedMissionValues.emit(outputMissionValues);
                        }
                        this.modal.hide();
                    },
                    error: (error: FlyFreelyError) => {
                        this.logging.error(
                            error,
                            `Error while updating location: ${error.message}`
                        );
                    }
                })
                .add(this.workTracker.createTracker());
        } else {
            const createCommand: CreateLocationCommand = {
                name,
                features,
                organisationId: this.organisationId,
                type: this.type,
                derivedFromId:
                    this.defaultValues?.id ??
                    this.defaultValues?.derivedFromId ??
                    null
            };
            this.locationService
                .createLocation(createCommand)
                .pipe(takeUntil(this.ngUnsubscribe$))
                .subscribe({
                    next: result => {
                        this.hasChanged = false;
                        this.formGroup.markAsPristine();
                        this.logging.success(
                            `Location created${
                                this.missionValues != null
                                    ? ' & mission details updated'
                                    : ''
                            }`
                        );
                        this.change.emit(result);
                        if (this.missionValues != null) {
                            this.updatedMissionValues.emit(outputMissionValues);
                        }
                        this.modal.hide();
                    },
                    error: (error: FlyFreelyError) => {
                        this.logging.error(
                            error,
                            `Error while saving location: ${error.message}`
                        );
                    }
                })
                .add(this.workTracker.createTracker());
        }
    }

    createMission() {
        const currentFeatureGroups =
            this.drawingComponent?.getFeatureGroups() ?? this.featureGroups;
        if (currentFeatureGroups[0].existingFeatures.length === 0) {
            return;
        }
        const geom = currentFeatureGroups[0].existingFeatures[0].geom;
        if (geom.type !== 'Polygon') {
            return;
        }

        const value = this.formGroup.value;
        const name = value.name;
        const features = this.getFeatures();

        const createCommand: CreateLocationCommand = {
            name,
            features,
            organisationId: this.organisationId,
            type: CreateLocationCommand.Type.MISSION,
            derivedFromId:
                this.defaultValues?.id ??
                this.defaultValues?.derivedFromId ??
                null
        };

        this.locationEditService.createMission(
            this.organisationId,
            this.airspaceValue$.getValue(),
            createCommand
        );
    }

    private getFeatures(): LocationFeatureDto[] {
        return (
            this.drawingComponent?.getFeatureGroups() ?? this.featureGroups
        ).reduce(
            (acc, fg) =>
                acc.concat(
                    fg.existingFeatures.reduce(
                        (acc2, f) =>
                            acc2.concat({
                                name: f.name,
                                geometry: f.geom,
                                type: f.categoryId,
                                properties: f.properties
                            }),
                        []
                    )
                ),
            []
        );
    }

    downloadKml() {
        const name = this.formGroup.value.name ?? 'Location';
        this.locationService
            .downloadKmlFromFeatures({ name, features: this.getFeatures() })
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: download => {
                    const headers = download.headers;
                    const fileName =
                        getFileName(headers.get('content-disposition')) ??
                        'flyfreely.kml';

                    FileSaver.saveAs(download.body, fileName);
                },
                error: (error: FlyFreelyError) =>
                    this.logging.error(
                        error,
                        `Error downloading KML file: ${error.message}`
                    )
            })
            .add(this.workTracker.createTracker());
    }

    close() {
        this.drawingComponent?.handleEditOnClose();
        this.modal.hide();
    }
}
